import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../components/ui/navigation-menu';
import axios from 'axios';
import {
  Briefcase,
  User,
  LogOut,
  Menu,
  Search,
  MailWarning,
  X,
  Building2,
  FileText,
  Bell,
  LayoutDashboard,
  Settings,
  ChevronDown,
  GraduationCap,
  Globe,
  Users,
  BookOpen,
  TrendingUp,
  Award,
  CheckCircle2,
  MessageSquare,
  Bookmark,
  ClipboardList,
  Newspaper,
  Sparkles,
  ShieldCheck
} from 'lucide-react';
import AdBlock from './AdBlock';
import NotificationBell from './NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Navbar = () => {
  const { user, token, logout, isAuthenticated, isJobSeeker, isEmployer, isCourseProvider, isUniversityManager, isEventHost, isRecruiter, branding, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState(false);
  const [resendingVerify, setResendingVerify] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const unreadPollRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) { setUnreadCount(0); return; }
    const poll = () => api.get('/chat/unread-count').then(r => setUnreadCount(r.data.count || 0)).catch(() => {});
    poll();
    unreadPollRef.current = setInterval(poll, 30000);
    return () => clearInterval(unreadPollRef.current);
  }, [isAuthenticated]);

  const showVerifyBanner = isAuthenticated && user && !user.email_verified && !verifyBannerDismissed;

  const handleResendVerification = async () => {
    setResendingVerify(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      import('sonner').then(({ toast }) => toast.success('Verification email sent! Check your inbox.'));
    } catch {
      import('sonner').then(({ toast }) => toast.error('Could not send email. Please try again.'));
    } finally {
      setResendingVerify(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
    <nav className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            {branding?.logo_url ? (
              <img
                src={
                  branding.logo_url.startsWith('http')
                    ? branding.logo_url
                    : `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`
                }
                alt={branding.platform_name || 'Logo'}
                className="h-10 w-auto object-contain"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-[#003366] flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-bold text-xl text-[#003366] hidden sm:block">{branding?.platform_name || 'WorksRun'}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Find Jobs */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent">Find Jobs</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[400px] p-4">
                      <div className="grid gap-3">
                        <Link to="/jobs" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <Search className="w-5 h-5 text-[#003366]" />
                          <div>
                            <div className="font-medium">Search Jobs</div>
                            <div className="text-sm text-muted-foreground">Browse all job listings</div>
                          </div>
                        </Link>
                        <Link to="/jobs?remote=true" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <Building2 className="w-5 h-5 text-[#003366]" />
                          <div>
                            <div className="font-medium">Remote Jobs</div>
                            <div className="text-sm text-muted-foreground">Work from anywhere</div>
                          </div>
                        </Link>
                        <Link to="/salary-checker" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <TrendingUp className="w-5 h-5 text-[#003366]" />
                          <div>
                            <div className="font-medium">Salary Checker</div>
                            <div className="text-sm text-muted-foreground">Compare salaries</div>
                          </div>
                        </Link>
                        <Link to="/interview-prep" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <Award className="w-5 h-5 text-[#003366]" />
                          <div>
                            <div className="font-medium">Interview Prep</div>
                            <div className="text-sm text-muted-foreground">Practice & AI tips</div>
                          </div>
                        </Link>
                        <Link to="/skills-tests" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          <div>
                            <div className="font-medium">Skills Tests</div>
                            <div className="text-sm text-muted-foreground">Validate your skills</div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Courses */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent">Courses</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[400px] p-4">
                      <div className="grid gap-3">
                        <Link to="/courses" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <GraduationCap className="w-5 h-5 text-emerald-600" />
                          <div>
                            <div className="font-medium">Browse Courses</div>
                            <div className="text-sm text-muted-foreground">Find your perfect course</div>
                          </div>
                        </Link>
                        <Link to="/courses?free=true" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <BookOpen className="w-5 h-5 text-emerald-600" />
                          <div>
                            <div className="font-medium">Free Courses</div>
                            <div className="text-sm text-muted-foreground">Learn at no cost</div>
                          </div>
                        </Link>
                        <Link to="/student-services" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <Globe className="w-5 h-5 text-amber-500" />
                          <div>
                            <div className="font-medium">Study in UK</div>
                            <div className="text-sm text-muted-foreground">International student support</div>
                          </div>
                        </Link>
                        <Link to="/universities" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <Building2 className="w-5 h-5 text-blue-600" />
                          <div>
                            <div className="font-medium">Universities</div>
                            <div className="text-sm text-muted-foreground">Browse UK institutions</div>
                          </div>
                        </Link>
                        <Link to="/course-providers" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <Sparkles className="w-5 h-5 text-purple-600" />
                          <div>
                            <div className="font-medium">Course Providers/Trainers</div>
                            <div className="text-sm text-muted-foreground">Short courses, workshops & training</div>
                          </div>
                        </Link>
                        {isCourseProvider && (
                          <Link to="/course-provider/dashboard" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary bg-emerald-50">
                            <GraduationCap className="w-5 h-5 text-emerald-600" />
                            <div>
                              <div className="font-medium text-emerald-700">Provider Dashboard</div>
                              <div className="text-sm text-muted-foreground">Manage your courses</div>
                            </div>
                          </Link>
                        )}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Career Advice */}
            <Link
              to="/career-advice"
              className={`px-3 py-2 text-sm font-medium transition-colors hover:text-[#003366] ${
                isActive('/career-advice') ? 'text-[#003366]' : 'text-muted-foreground'
              }`}
            >
              Career Advice
            </Link>

            {/* Events */}
            <Link
              to="/events"
              className={`px-3 py-2 text-sm font-medium transition-colors hover:text-[#003366] ${
                isActive('/events') ? 'text-[#003366]' : 'text-muted-foreground'
              }`}
            >
              Events
            </Link>

            {/* Blog */}
            <Link
              to="/blog"
              className={`px-3 py-2 text-sm font-medium transition-colors hover:text-[#003366] ${
                isActive('/blog') ? 'text-[#003366]' : 'text-muted-foreground'
              }`}
            >
              Blog
            </Link>

            {/* Help */}
            <Link
              to="/help"
              className={`px-3 py-2 text-sm font-medium transition-colors hover:text-[#003366] ${
                isActive('/help') ? 'text-[#003366]' : 'text-muted-foreground'
              }`}
            >
              Help
            </Link>

            {/* Recruiters Directory */}
            <Link
              to="/recruiters"
              className={`px-3 py-2 text-sm font-medium transition-colors hover:text-[#003366] ${
                isActive('/recruiters') ? 'text-[#003366]' : 'text-muted-foreground'
              }`}
            >
              Recruiters
            </Link>

            {/* Pricing */}
            <Link
              to="/pricing"
              className={`px-3 py-2 text-sm font-medium transition-colors hover:text-[#003366] ${
                isActive('/pricing') ? 'text-[#003366]' : 'text-muted-foreground'
              }`}
            >
              Pricing
            </Link>

            {/* For Employers */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent">For Employers</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[400px] p-4">
                      <div className="grid gap-3">
                        <Link to="/recruiter" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <Users className="w-5 h-5 text-[#003366]" />
                          <div>
                            <div className="font-medium">Recruitment Solutions</div>
                            <div className="text-sm text-muted-foreground">Find the right talent</div>
                          </div>
                        </Link>
                        <Link to="/recruiters" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <Users className="w-5 h-5 text-[#003366]" />
                          <div>
                            <div className="font-medium">Recruiters Directory</div>
                            <div className="text-sm text-muted-foreground">Browse recruiters and their jobs</div>
                          </div>
                        </Link>
                        <Link to="/direct-employment-solutions" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <ShieldCheck className="w-5 h-5 text-[#003366]" />
                          <div>
                            <div className="font-medium">Direct Employment Solutions</div>
                            <div className="text-sm text-muted-foreground">Custom recruitment requests, managed by our team</div>
                          </div>
                        </Link>
                        <Link to="/employer/post-job" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                          <Briefcase className="w-5 h-5 text-[#003366]" />
                          <div>
                            <div className="font-medium">Post a Job</div>
                            <div className="text-sm text-muted-foreground">Advertise your vacancies</div>
                          </div>
                        </Link>
                        {isEmployer && (
                          <Link to="/employer/dashboard" className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                            <LayoutDashboard className="w-5 h-5 text-[#003366]" />
                            <div>
                              <div className="font-medium">Employer Dashboard</div>
                              <div className="text-sm text-muted-foreground">Manage your listings</div>
                            </div>
                          </Link>
                        )}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                {/* Dashboard Link */}
                <Link
                  to={
                    user?.user_type === 'admin' ? '/admin' :
                    isEmployer ? '/employer/dashboard' :
                    isCourseProvider ? '/course-provider/dashboard' :
                    isUniversityManager ? '/university/dashboard' :
                    isEventHost ? '/event-host/dashboard' :
                    isRecruiter ? '/recruiter-dashboard' :
                    '/dashboard'
                  }
                  className="hidden md:flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-[#003366] transition-colors"
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Link>

                <NotificationBell />

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-2 px-3 relative"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#003366] flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {user?.full_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="hidden lg:block text-sm font-medium max-w-[120px] truncate">
                        {user?.full_name}
                      </span>
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#E11D48] rounded-full border-2 border-white" />
                      )}
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={
                        user?.user_type === 'admin' ? '/admin' :
                        isEmployer ? '/employer/dashboard' :
                        isCourseProvider ? '/course-provider/dashboard' :
                        isUniversityManager ? '/university/dashboard' :
                        isEventHost ? '/event-host/dashboard' :
                        isRecruiter ? '/recruiter-dashboard' :
                        '/dashboard'
                      } className="flex items-center">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center">
                        <User className="w-4 h-4 mr-2" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    {isJobSeeker && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/cv-builder" className="flex items-center">
                            <FileText className="w-4 h-4 mr-2" />
                            CV Builder
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/alerts" className="flex items-center">
                            <Bell className="w-4 h-4 mr-2" />
                            Job Alerts
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/saved-jobs" className="flex items-center">
                            <Bookmark className="w-4 h-4 mr-2" />
                            Saved Jobs
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/my-applications" className="flex items-center">
                            <ClipboardList className="w-4 h-4 mr-2" />
                            My Applications
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/messages" className="flex items-center justify-between w-full" onClick={() => setUnreadCount(0)}>
                        <span className="flex items-center">
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Messages
                        </span>
                        {unreadCount > 0 && (
                          <span className="ml-2 bg-[#E11D48] text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {user?.user_type === 'admin' && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center">
                          <Settings className="w-4 h-4 mr-2" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="font-medium hidden sm:flex">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button className="bg-[#E11D48] hover:bg-[#E11D48]/90 text-white rounded-full px-4 sm:px-6">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <div className="flex flex-col gap-4 mt-8">
                  <Link
                    to="/jobs"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                  >
                    <Search className="w-5 h-5 text-[#003366]" />
                    <span className="font-medium">Find Jobs</span>
                  </Link>
                  <Link
                    to="/courses"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                  >
                    <GraduationCap className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium">Courses</span>
                  </Link>
                  <Link to="/universities" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Universities</span>
                  </Link>
                  <Link to="/course-providers" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">Course Providers/Trainers</span>
                  </Link>
                  <Link
                    to="/career-advice"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                  >
                    <Award className="w-5 h-5 text-[#003366]" />
                    <span className="font-medium">Career Advice</span>
                  </Link>
                  <Link
                    to="/student-services"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                  >
                    <Globe className="w-5 h-5 text-amber-500" />
                    <span className="font-medium">Student Services</span>
                  </Link>
                  <Link
                    to="/recruiter"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                  >
                    <Users className="w-5 h-5 text-[#003366]" />
                    <span className="font-medium">For Employers</span>
                  </Link>
                  <Link
                    to="/recruiters"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                  >
                    <Users className="w-5 h-5 text-[#003366]" />
                    <span className="font-medium">Recruiters Directory</span>
                  </Link>
                  <Link
                    to="/direct-employment-solutions"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                  >
                    <ShieldCheck className="w-5 h-5 text-[#003366]" />
                    <span className="font-medium">Direct Employment Solutions</span>
                  </Link>
                  <Link
                    to="/events"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                  >
                    <Award className="w-5 h-5 text-[#003366]" />
                    <span className="font-medium">Events</span>
                  </Link>
                  <Link
                    to="/pricing"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                  >
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium">Pricing</span>
                  </Link>
                  <Link to="/skills-tests" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium">Skills Tests</span>
                  </Link>
                  <Link to="/interview-prep" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                    <Award className="w-5 h-5 text-[#003366]" />
                    <span className="font-medium">Interview Prep</span>
                  </Link>
                  <Link to="/blog" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                    <Newspaper className="w-5 h-5 text-[#003366]" />
                    <span className="font-medium">Blog</span>
                  </Link>

                  {isAuthenticated && (
                    <>
                      <div className="border-t my-2" />
                      <Link
                        to={
                        isEmployer ? '/employer/dashboard' :
                        isCourseProvider ? '/course-provider/dashboard' :
                        isUniversityManager ? '/university/dashboard' :
                        isEventHost ? '/event-host/dashboard' :
                        isRecruiter ? '/recruiter-dashboard' :
                        '/dashboard'
                      }
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                      >
                        <LayoutDashboard className="w-5 h-5 text-[#003366]" />
                        <span className="font-medium">Dashboard</span>
                      </Link>
                      <Link
                        to="/profile"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary"
                      >
                        <User className="w-5 h-5 text-[#003366]" />
                        <span className="font-medium">Profile</span>
                      </Link>
                      <Link to="/messages" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                        <MessageSquare className="w-5 h-5 text-[#003366]" />
                        <span className="font-medium">Messages</span>
                      </Link>
                      {isJobSeeker && (
                        <>
                          <Link to="/saved-jobs" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                            <Bookmark className="w-5 h-5 text-[#003366]" />
                            <span className="font-medium">Saved Jobs</span>
                          </Link>
                          <Link to="/my-applications" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary">
                            <ClipboardList className="w-5 h-5 text-[#003366]" />
                            <span className="font-medium">My Applications</span>
                          </Link>
                        </>
                      )}
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
    {showVerifyBanner && (
      <div className="bg-amber-500 text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <MailWarning className="w-4 h-4 shrink-0" />
            <span>Please verify your email address to unlock all features.</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleResendVerification}
              disabled={resendingVerify}
              className="underline font-semibold hover:no-underline disabled:opacity-60"
            >
              {resendingVerify ? 'Sending…' : 'Resend email'}
            </button>
            <button onClick={() => setVerifyBannerDismissed(true)} className="ml-1 hover:opacity-75">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )}
    <AdBlock position="header" showEmpty />
    </>
  );
};

export default Navbar;
